import {
  AnyVariables,
  SubscriptionHandler,
  useSubscription as useUrqlSubscription,
  UseSubscriptionArgs as UrqlUseSubscriptionArgs,
} from "urql";

/** Recursively allows optional keys and undefined in values (for use when pause is true). */
type VariablesWithOptionalValues<V> = {
  [K in keyof V]?: V[K] extends object
    ? VariablesWithOptionalValues<V[K]>
    : V[K] | undefined;
};

/** When pause is true, variables may be omitted or partial (including undefined values) since the subscription does not run. */
type UseSubscriptionArgs<Variables extends AnyVariables, Data> = Omit<
  UrqlUseSubscriptionArgs<Variables, Data>,
  "variables" | "pause"
> & {
  pause?: boolean;
  /** Required when pause is false; optional when pause is true. Values may be undefined when paused. */
  variables?: VariablesWithOptionalValues<Variables> | Variables;
};

const useSubscriptionImpl = <
  Data = any,
  Result = Data,
  Variables extends AnyVariables = AnyVariables,
>(
  args: UseSubscriptionArgs<Variables, Data>,
  handler?: SubscriptionHandler<Data, Result>,
) => {
  const normalizedArgs: UrqlUseSubscriptionArgs<Variables, Data> = {
    ...args,
    variables: (args.variables ?? {}) as Variables,
  };
  return useUrqlSubscription<Data, Result, Variables>(normalizedArgs, handler);
};

type UseSubscription = {
  <Data = any, Result = Data, Variables extends AnyVariables = AnyVariables>(
    args: UseSubscriptionArgs<Variables, Data>,
    handler?: SubscriptionHandler<Data, Result>,
  ): ReturnType<typeof useSubscriptionImpl<Data, Result, Variables>>;
};

export const useSubscription: UseSubscription = (args, handler) =>
  useSubscriptionImpl(args, handler);
